import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, ArrowRight, ShieldCheck, Users, HeartHandshake, Hammer, Flame, Droplets, Info, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { FadeIn } from "../components/motion";

const NEEDS = [
  { icon: Flame, title: "Emergency shelter support", text: "Fire, flood, building collapse, sudden eviction, severe weather — temporary accommodation, shelter materials, essentials and referral to relevant services." },
  { icon: Home, title: "Rent & temporary accommodation", text: "Partial rent assistance, deposit help and short-term accommodation for households facing genuine housing crises." },
  { icon: Hammer, title: "Home repair support", text: "Roofing, doors and windows, structural defects, sanitation, water access, electrical safety and accessibility improvements." },
  { icon: ShieldCheck, title: "Rebuilding after disaster", text: "Emergency assessment, building material support, partial or complete reconstruction with approved scope, budget and monitoring." },
  { icon: Users, title: "Vulnerable family housing", text: "Substantial housing intervention for widows, elderly, orphan-led households, persons with disabilities and disaster-affected families." },
  { icon: Droplets, title: "Community shelter projects", text: "Emergency shelters, transitional housing and accommodation linked to approved social programmes." },
];

const CAN_APPLY = [
  "Individuals", "Families", "Widows or widowers", "Elderly persons",
  "Persons with disabilities", "Vulnerable households", "Displaced families",
  "Victims of fire or natural disaster", "Families in unsafe accommodation",
  "Community representatives", "Community organisations", "Schools & institutions", "Referring NGOs",
];

export default function Housing() {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    document.title = "Shelter & Housing Support Programme | Dutsen Kauna Foundation";
    api.get("/supported-projects").then((r) => setProjects(r.data.filter((p) => (p.project_name || "").toLowerCase().includes("hous") || (p.project_name || "").toLowerCase().includes("shelter") || (p.project_name || "").toLowerCase().includes("home")))).catch(() => {});
  }, []);

  return (
    <div data-testid="housing-page">
      {/* Hero */}
      <section className="relative bg-[#155e3f] text-white py-20 md:py-28 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600" alt="Community members rebuilding a family home after flooding" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] via-[#155e3f]/85 to-[#155e3f]/40" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.35em] text-xs font-semibold uppercase">Programme · Shelter & Housing</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold mt-3 max-w-4xl leading-[1.05]">A safe place to live. <span className="italic text-[#d6aa43]">Dignity to rebuild.</span></h1>
          <p className="mt-6 text-white text-lg max-w-3xl">
            Support for vulnerable individuals, families and communities facing homelessness, unsafe housing,
            displacement or urgent shelter needs — from emergency response through construction, monitoring and follow-up.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="housing-cta-row">
            <Link to="/apply?category=housing" className="dk-btn-primary" data-testid="housing-apply">
              Apply for housing support <ArrowRight size={16} />
            </Link>
            <Link to="/donate?programme=shelter" className="dk-btn-outline" data-testid="housing-donate">
              Donate to shelter
            </Link>
            <Link to="/international#partner-form" className="dk-btn-outline !border-white/60 !text-white hover:!bg-white hover:!text-[#0d1724]" data-testid="housing-sponsor">
              Sponsor a housing project
            </Link>
          </div>
          <div className="mt-6 text-[13px] text-white"><Info size={14} className="inline text-[#d6aa43]" /> Application is free. Every case receives a unique <span className="font-mono text-[#d6aa43]">DKF-HOU-YYYY-XXXXXX</span> serial — trackable end-to-end from application through site assessment, funding, construction and long-term follow-up.</div>
        </div>
      </section>

      {/* Who can apply */}
      <section className="py-16 bg-white">
        <div className="dk-container">
          <FadeIn>
            <div className="max-w-3xl">
              <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Who can apply</span>
              <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">Applications welcomed from those most at risk.</h2>
            </div>
          </FadeIn>
          <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {CAN_APPLY.map((t) => (
              <div key={t} className="p-4 rounded-md bg-[#f8f4e8] border border-black/5 text-sm font-semibold text-[#0a0d12] flex items-center gap-2">
                <CheckCircle2 size={14} className="text-[#d6aa43] flex-shrink-0" /> {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Housing support areas */}
      <section className="py-20 bg-[#155e3f] text-white">
        <div className="dk-container">
          <FadeIn>
            <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Our housing support areas</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 mb-10 max-w-3xl">Six routes to safer, more dignified shelter.</h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {NEEDS.map((n, i) => {
              const Icon = n.icon;
              return (
                <div key={n.title} className="p-6 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition" data-testid={`housing-area-${i}`}>
                  <div className="w-11 h-11 rounded-full bg-[#d6aa43] text-[#0a0d12] flex items-center justify-center mb-4"><Icon size={20} /></div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#d6aa43] font-bold">0{i + 1}</div>
                  <h3 className="font-serif text-xl font-bold mt-1 mb-2">{n.title}</h3>
                  <p className="text-sm text-white leading-relaxed">{n.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Build a Home spotlight */}
      <section className="py-20 bg-[#f8f4e8]">
        <div className="dk-container">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <FadeIn>
              <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Build a Home programme</span>
              <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3 mb-4">Sponsor a safe home for a vulnerable family.</h2>
              <p className="text-[#0a0d12]/80 mb-6">
                Individuals, companies, diaspora groups, faith organisations and institutional partners may sponsor the
                construction or rehabilitation of an approved home. Every project has a dedicated public page with
                scope, budget, procurement records and monitoring milestones.
              </p>
              <div className="p-6 rounded-xl bg-white border border-[#d6aa43]/20">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d6aa43] font-bold">Example project</div>
                <div className="font-mono text-sm mt-1 text-[#0a0d12]">DKF-HOU-2026-00018</div>
                <h3 className="font-serif text-xl text-[#0a0d12] font-bold mt-2">Two-bedroom basic home</h3>
                <div className="text-sm text-[#0a0d12]/70 mt-1">Hong LGA, Adamawa State · Vulnerable family (6 members)</div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-[#f8f4e8] overflow-hidden">
                    <div className="h-full bg-[#d6aa43]" style={{ width: "35%" }} />
                  </div>
                  <div className="text-xs font-bold text-[#0a0d12]">35% funded</div>
                </div>
                <Link to="/international#partner-form" className="dk-btn-primary mt-4 text-sm !py-2">Fund this home <ArrowRight size={14} /></Link>
              </div>
            </FadeIn>
            <div className="grid grid-cols-2 gap-3">
              <img src="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800" alt="Community members rebuilding a family home after flooding" loading="lazy" decoding="async" className="rounded-xl h-72 w-full object-cover" />
              <img src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800" alt="Community members rebuilding a family home after flooding" loading="lazy" decoding="async" className="rounded-xl h-72 w-full object-cover mt-8" />
            </div>
          </div>
        </div>
      </section>

      {/* Application flow */}
      <section className="py-16 bg-white">
        <div className="dk-container max-w-4xl">
          <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Apply for shelter or housing support</span>
          <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3 mb-8">One connected serial. Every step tracked.</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-8">
            {[
              "Application", "Site assessment", "Verification",
              "Funding & approval", "Construction / repair", "Handover & long-term follow-up",
            ].map((s, i) => (
              <div key={s} className="p-4 rounded-md bg-[#f8f4e8] border border-black/5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#d6aa43] font-bold">Step 0{i + 1}</div>
                <div className="font-serif text-lg text-[#0a0d12] font-bold mt-1">{s}</div>
              </div>
            ))}
          </div>
          <div className="p-6 rounded-xl bg-[#155e3f] text-white flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <HeartHandshake className="text-[#d6aa43] flex-shrink-0" size={28} />
            <div className="flex-1">
              <div className="font-serif text-xl font-bold">Ready to apply?</div>
              <p className="text-sm text-white">Start the multi-step application. You'll receive a <span className="font-mono text-[#d6aa43]">DKF-HOU</span> serial and can track progress from your applicant portal.</p>
            </div>
            <Link to="/apply?category=housing" className="dk-btn-primary" data-testid="housing-apply-cta">
              Start application <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Public supported housing projects */}
      {projects.length > 0 && (
        <section className="py-16 bg-[#f8f4e8]">
          <div className="dk-container">
            <h2 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mb-8">Active housing projects</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {projects.map((p) => (
                <div key={p.id} className="p-6 rounded-xl bg-white border border-black/5" data-testid={`hp-${p.id}`}>
                  {p.serial && <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#d6aa43] font-bold mb-1">{p.serial}</div>}
                  <h3 className="font-serif text-xl text-[#0a0d12] font-bold">{p.project_name}</h3>
                  <div className="text-xs text-[#0a0d12]/60 mt-1">{p.location}</div>
                  <div className="text-sm text-[#0a0d12]/80 mt-3">{p.amount_value || (p.amount_numeric ? `${p.currency} ${Number(p.amount_numeric).toLocaleString()}` : "—")}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
